import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGetUserNotifications, useMarkNotificationRead, useMarkAllNotificationsRead } from '../../custom-hooks/use-notifications';
import moment from 'moment';
import {
    Bell,
    CheckCheck,
    LifeBuoy,
    CalendarCheck,
    FileText,
    Clock,
    Megaphone,
    AlertCircle,
    Loader2,
} from 'lucide-react';

moment.updateLocale('en-short', {
    relativeTime: {
        future: 'in %s',
        past: '%s ago',
        s: 'just now',
        ss: '%ds',
        m: '1m',
        mm: '%dm',
        h: '1h',
        hh: '%dh',
        d: '1d',
        dd: '%dd',
        M: '1mo',
        MM: '%dmo',
        y: '1y',
        yy: '%dy'
    }
});

// Broad notification categories derived from the granular notificationType.
// Extend TYPE_TO_CATEGORY as the backend introduces new notification types.
const TYPE_TO_CATEGORY = {
    ticket_created: 'support',
    status_updated: 'support',
    agent_assigned: 'support',
    new_message: 'support',
    resolved_pending_ack: 'support',
    escalation_alert: 'support',
    contract_expiring: 'contracts',
    contract_approved: 'contracts',
    contract_activated: 'contracts',
    booking_confirmed: 'bookings',
    booking_cancelled: 'bookings',
    booking_reminder: 'bookings',
    quota_running_low: 'quota',
    quota_reset: 'quota',
};

const CATEGORY_CONFIG = {
    support: { label: 'Support', icon: LifeBuoy, iconBg: '#F8E7D8', iconColor: '#BE5310', border: '#BE5310' },
    bookings: { label: 'Bookings', icon: CalendarCheck, iconBg: '#E4F1EA', iconColor: '#2F9E68', border: '#2F9E68' },
    contracts: { label: 'Contracts', icon: FileText, iconBg: '#E3EDF5', iconColor: '#3B6EA5', border: '#3B6EA5' },
    quota: { label: 'Quota', icon: Clock, iconBg: '#FBEFD4', iconColor: '#B07A12', border: '#B07A12' },
    system: { label: 'System', icon: Megaphone, iconBg: '#EFE9F5', iconColor: '#7A5BA8', border: '#7A5BA8' },
};

const CATEGORY_ORDER = ['support', 'bookings', 'contracts', 'quota', 'system'];

const getCategoryKey = (type) => TYPE_TO_CATEGORY[type] || 'system';

const FILTER_TABS = [
    { key: 'all', label: 'All' },
    { key: 'unread', label: 'Unread' },
];

const NotificationCard = ({ notification, onMarkRead, isMarking }) => {
    const cat = CATEGORY_CONFIG[getCategoryKey(notification.notificationType)];
    const Icon = cat.icon;
    const isUnread = !notification.readAt;
    const navigate = useNavigate();

    const handleCardClick = () => {
        let url = notification.actionUrl;
        if (url) {
            // Handle legacy URLs
            if (url.startsWith('/admin/tickets')) url = url.replace('/admin/tickets', '/support');
            if (url.startsWith('/tickets')) url = url.replace('/tickets', '/support');

            // Map support to team member portal
            if (url.startsWith('/support')) url = `/tm${url}`;

            if (isUnread) onMarkRead(notification._id);
            navigate(url);
        }
    };

    return (
        <div
            onClick={handleCardClick}
            className={`group relative flex items-start gap-3.5 overflow-hidden rounded-[14px] border border-[#ECE3D5] py-4 pl-5 pr-[18px] transition-[box-shadow,transform,background-color] duration-200 hover:-translate-y-px hover:border-[#E0C3A4] hover:shadow-[0_14px_30px_-20px_rgba(40,22,8,0.4)] ${notification.actionUrl ? 'cursor-pointer' : ''} ${isUnread ? 'bg-white shadow-[0_2px_10px_-6px_rgba(40,22,8,0.2)]' : 'bg-[#FBF6EF]'}`}
        >
            {/* Category accent bar */}
            <span className="absolute inset-y-0 left-0 w-1" style={{ backgroundColor: cat.border }} />
            <div
                className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl"
                style={{ backgroundColor: cat.iconBg, color: cat.iconColor }}
            >
                <Icon size={19} />
            </div>
            <div className="min-w-0 flex-1">
                <div className="mb-1 flex flex-wrap items-center gap-2">
                    <h4 className={`text-sm font-semibold ${isUnread ? 'text-ink' : 'text-[#6B675F]'}`}>{notification.title}</h4>
                    <span
                        className="rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide"
                        style={{ backgroundColor: cat.iconBg, color: cat.iconColor }}
                    >
                        {cat.label}
                    </span>
                </div>
                <p className={`text-sm leading-relaxed ${isUnread ? 'text-[#6B675F]' : 'text-[#A89C8C]'}`}>{notification.message}</p>
                <span className="mt-1.5 flex items-center gap-1 text-xs text-[#A89C8C]">
                    <Clock size={12} /> {moment(notification.createdDate).fromNow()}
                </span>
            </div>
            <div className="flex shrink-0 flex-col items-end gap-2">
                {isUnread && <span className="mt-1 h-2 w-2 rounded-full" style={{ backgroundColor: cat.border }} />}
                {isUnread && (
                    <button
                        onClick={(e) => { e.stopPropagation(); onMarkRead(notification._id); }}
                        disabled={isMarking}
                        className="text-xs font-medium text-[#8C8175] opacity-0 transition-opacity hover:text-brand disabled:opacity-50 group-hover:opacity-100"
                    >
                        {isMarking ? 'Marking...' : 'Mark read'}
                    </button>
                )}
            </div>
        </div>
    );
};

const TeamMemberNotifications = () => {
    const { data: notifications, isLoading, error } = useGetUserNotifications();
    const markNotificationReadMutation = useMarkNotificationRead();
    const markAllReadMutation = useMarkAllNotificationsRead();
    const [markingId, setMarkingId] = useState(null);
    const [activeFilter, setActiveFilter] = useState('all');

    const allNotifications = useMemo(() => notifications?.notifications || [], [notifications]);

    const notificationList = useMemo(() => {
        return activeFilter === 'unread' ? allNotifications.filter(n => !n.readAt) : allNotifications;
    }, [allNotifications, activeFilter]);

    const unreadCount = useMemo(() => allNotifications.filter(n => !n.readAt).length, [allNotifications]);
    const totalCount = allNotifications.length;
    const readCount = totalCount - unreadCount;

    const groups = useMemo(() => {
        const today = [];
        const earlier = [];
        notificationList.forEach((n) => {
            if (moment(n.createdDate).isSame(moment(), 'day')) today.push(n);
            else earlier.push(n);
        });
        return { today, earlier };
    }, [notificationList]);

    const categoryStats = useMemo(() => {
        const acc = {};
        allNotifications.forEach((n) => {
            const key = getCategoryKey(n.notificationType);
            if (!acc[key]) acc[key] = { total: 0, unread: 0 };
            acc[key].total += 1;
            if (!n.readAt) acc[key].unread += 1;
        });
        return CATEGORY_ORDER.filter((k) => acc[k]).map((k) => ({ key: k, ...CATEGORY_CONFIG[k], ...acc[k] }));
    }, [allNotifications]);

    const handleMarkRead = async (notificationId) => {
        try { setMarkingId(notificationId); await markNotificationReadMutation.mutateAsync({ notificationId }); }
        catch (err) { console.error(err); }
        finally { setMarkingId(null); }
    };

    const handleMarkAllRead = async () => {
        try { await markAllReadMutation.mutateAsync(); }
        catch (err) { console.error(err); }
    };

    if (isLoading) return <div className="flex h-[60vh] items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-brand" /></div>;

    if (error) return (
        <div className="animate-fadeIn px-4 py-5 md:px-[34px] md:py-[30px]">
            <div className="flex flex-col items-center justify-center rounded-2xl border border-[#ECE3D5] bg-white py-16 text-center">
                <AlertCircle size={32} className="mb-3 text-red-400" />
                <h3 className="text-base font-semibold text-ink">Something went wrong</h3>
                <p className="mt-1 text-sm text-[#8C8175]">Unable to load notifications.</p>
            </div>
        </div>
    );

    const renderSection = (label, items) => items.length > 0 && (
        <div>
            <div className="mb-3 ml-0.5 flex items-center gap-2.5">
                <span className="text-[11px] font-bold uppercase tracking-[0.08em] text-[#A89C8C]">{label}</span>
                <span className="h-px flex-1 bg-[#ECE3D5]" />
            </div>
            <div className="flex flex-col gap-2.5">
                {items.map((n) => (
                    <NotificationCard key={n._id} notification={n} onMarkRead={handleMarkRead} isMarking={markingId === n._id} />
                ))}
            </div>
        </div>
    );

    return (
        <div className="animate-fadeIn px-4 py-5 md:px-[34px] md:py-[30px]">
            {/* Header */}
            <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                <div>
                    <h1 className="font-display text-[25px] font-bold text-ink">Notifications</h1>
                    <p className="mt-1 text-sm text-[#8C8175]">Stay on top of bookings, contracts and support.</p>
                </div>
                <div className="flex flex-wrap items-center gap-2.5">
                    <div className="relative grid min-w-[240px] grid-cols-2 rounded-xl border border-[#ECE3D5] bg-white p-[5px]">
                        <span
                            className="pointer-events-none absolute inset-y-[5px] left-[5px] w-[calc(50%-5px)] rounded-[9px] bg-brand shadow-[0_6px_14px_-8px_rgba(190,83,16,0.7)] transition-transform duration-[340ms] ease-[cubic-bezier(.4,0,.2,1)]"
                            style={{ transform: activeFilter === 'unread' ? 'translateX(100%)' : 'translateX(0%)' }}
                        />
                        {FILTER_TABS.map((tab) => {
                            const active = activeFilter === tab.key;
                            return (
                                <button
                                    key={tab.key}
                                    onClick={() => setActiveFilter(tab.key)}
                                    className={`relative z-10 flex items-center justify-center gap-1.5 rounded-[9px] px-3.5 py-2 text-[13px] font-semibold transition-colors ${active ? 'text-white' : 'text-[#6B675F] hover:text-ink'}`}
                                >
                                    {tab.label}
                                    {tab.key === 'unread' && unreadCount > 0 && (
                                        <span className="rounded-full bg-black/[0.08] px-[7px] py-px text-[11px] font-bold">
                                            {unreadCount}
                                        </span>
                                    )}
                                </button>
                            );
                        })}
                    </div>
                    {unreadCount > 0 && (
                        <button
                            onClick={handleMarkAllRead}
                            disabled={markAllReadMutation.isLoading}
                            className="flex items-center gap-1.5 rounded-[11px] border-[1.5px] border-[#E6DDCF] bg-white px-4 py-2.5 font-display text-[13px] font-semibold text-[#3B2A1E] transition-colors hover:bg-cream disabled:opacity-60"
                        >
                            {markAllReadMutation.isLoading ? <Loader2 size={16} className="animate-spin" /> : <CheckCheck size={16} />}
                            Mark all as read
                        </button>
                    )}
                </div>
            </div>

            <div className="grid grid-cols-1 gap-[22px] lg:grid-cols-[minmax(0,1fr)_320px]">
                {/* List */}
                <div key={activeFilter} className="animate-fadeIn">
                    {notificationList.length === 0 ? (
                        <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-[#E6DDCF] bg-white py-16 text-center">
                            <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-[#E4F1EA] text-green-600">
                                <CheckCheck size={26} />
                            </div>
                            {activeFilter === 'unread' ? (
                                <>
                                    <h3 className="font-display text-lg font-bold text-ink">You&rsquo;re all caught up</h3>
                                    <p className="mt-1 text-sm text-[#8C8175]">No unread notifications right now.</p>
                                    <button
                                        onClick={() => setActiveFilter('all')}
                                        className="mt-4 rounded-xl border border-[#ECE3D5] bg-white px-4 py-2 text-sm font-semibold text-ink transition-colors hover:bg-cream-input"
                                    >
                                        View all notifications
                                    </button>
                                </>
                            ) : (
                                <>
                                    <h3 className="font-display text-lg font-bold text-ink">No notifications yet</h3>
                                    <p className="mt-1 text-sm text-[#8C8175]">When something happens, we&rsquo;ll let you know here.</p>
                                </>
                            )}
                        </div>
                    ) : (
                        <div className="flex flex-col gap-[22px]">
                            {renderSection('Today', groups.today)}
                            {renderSection('Earlier', groups.earlier)}
                        </div>
                    )}
                </div>

                {/* Right rail */}
                <aside className="space-y-4">
                    <div
                        className="relative overflow-hidden rounded-2xl p-6 text-white"
                        style={{ background: 'linear-gradient(135deg, #C9651B 0%, #9A480E 100%)' }}
                    >
                        <div className="pointer-events-none absolute -right-6 -top-6 h-28 w-28 rounded-full border border-white/20" />
                        <div className="absolute right-4 top-4 text-white/85"><Bell size={26} /></div>
                        <div className="text-xs font-semibold uppercase tracking-wider text-white/80">Unread</div>
                        <div className="mt-2 font-display text-[44px] font-bold leading-none">{unreadCount}</div>
                        <div className="mt-2 text-[12.5px] text-white/85">{readCount} read · {totalCount} total</div>
                    </div>

                    {categoryStats.length > 0 && (
                        <div className="rounded-2xl border border-[#ECE3D5] bg-white p-5">
                            <div className="mb-3 text-[11px] font-bold uppercase tracking-wider text-[#8C8175]">By category</div>
                            <div className="flex flex-col gap-1.5">
                                {categoryStats.map((c) => {
                                    const Icon = c.icon;
                                    return (
                                        <div key={c.key} className="flex items-center gap-3 rounded-[11px] px-2.5 py-2 transition-colors hover:bg-cream">
                                            <div
                                                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg"
                                                style={{ backgroundColor: c.iconBg, color: c.iconColor }}
                                            >
                                                <Icon size={18} />
                                            </div>
                                            <div className="min-w-0 flex-1">
                                                <div className="font-display text-[13.5px] font-semibold text-ink">{c.label}</div>
                                                <div className="text-[11.5px] text-[#A89C8C]">{c.total} total</div>
                                            </div>
                                            {c.unread > 0 && (
                                                <span className="rounded-full bg-cream-input px-2.5 py-0.5 text-[11px] font-bold text-[#9F440B]">
                                                    {c.unread}
                                                </span>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </aside>
            </div>
        </div>
    );
};

export default TeamMemberNotifications;
